import {
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  Table as ChakraTable,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  SortDirection,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown, ChevronUp, Scan } from 'lucide-react'
import { useState } from 'react'

interface Props<T> {
  data: T[]
  columns: ColumnDef<T>[]
  initialSorting?: SortingState
  initialState?: InitialTableState
  showFooter?: boolean
}

export function Table<T>({
  data,
  columns,
  initialSorting,
  initialState,
  showFooter,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? [])
  const [filtering, setFiltering] = useState('')

  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
    state: {
      sorting,
      globalFilter: filtering,
    },
    initialState: initialState ?? {
      pagination: {
        pageSize: 10,
      },
    },
  })

  const needPagination = table.getPageCount() > 1
  const currentPage = table.getState().pagination.pageIndex + 1
  const pageCount = table.getPageCount()

  const headerGroups = table.getHeaderGroups()
  const { rows } = table.getRowModel()

  function scrollTop() {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' })
  }

  const pageHandler = {
    first: () => {
      table.setPageIndex(0)
      scrollTop()
    },
    previos: () => {
      table.previousPage()
      scrollTop()
    },
    next: () => {
      table.nextPage()
      scrollTop()
    },
    last: () => {
      table.setPageIndex(table.getPageCount() - 1)
      scrollTop()
    },
  }

  return (
    <Box>
      <Grid overflow="hidden">
        <Box overflowX="auto">
          <ChakraTable variant="simple">
            <Thead>
              {headerGroups.map((header) => {
                return (
                  <Tr key={header.id}>
                    {header.headers.map((header) => {
                      return (
                        <Th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          bg="gray.100"
                          fontFamily="body"
                          fontWeight="normal"
                          cursor="pointer"
                        >
                          <HStack gap={2}>
                            <Text>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                            </Text>
                            {
                              {
                                asc: <Icon as={ChevronUp} />,
                                desc: <Icon as={ChevronDown} />,
                              }[header.column.getIsSorted() as SortDirection]
                            }
                          </HStack>
                        </Th>
                      )
                    })}
                  </Tr>
                )
              })}
            </Thead>

            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>

            {showFooter && (
              <Tfoot>
                {headerGroups.map((header) => {
                  return (
                    <Tr key={header.id}>
                      {header.headers.map((header) => {
                        return (
                          <Th
                            key={header.id}
                            bg="gray.50"
                            fontFamily="body"
                            fontSize="sm"
                          >
                            {flexRender(
                              header.column.columnDef.footer,
                              header.getContext(),
                            )}
                          </Th>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Tfoot>
            )}
          </ChakraTable>
        </Box>
      </Grid>

      {needPagination && (
        <VStack mt={8} align="center">
          <HStack gap={2} justifyItems="center">
            <Button variant="outline" size="xs" onClick={pageHandler.first}>
              Primeira
            </Button>
            <Button
              variant="outline"
              size="xs"
              isDisabled={!table.getCanPreviousPage()}
              onClick={pageHandler.previos}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="xs"
              isDisabled={!table.getCanNextPage()}
              onClick={pageHandler.next}
            >
              Próxima
            </Button>
            <Button variant="outline" size="xs" onClick={pageHandler.last}>
              Última
            </Button>
          </HStack>

          <Text color="gray.400" fontSize="sm">
            Página {currentPage} de {pageCount}
          </Text>
        </VStack>
      )}

      {data.length === 0 ? (
        <HStack justifyContent="center" gap={2} bg="gray.50" p={4}>
          <Icon as={Scan} strokeWidth={1} />
          <Text fontSize="sm">Nenhum dado encontrado</Text>
        </HStack>
      ) : (
        <Box textAlign="center" fontSize="xs" color="gray.400" p={2}>
          <Text>
            Total de {data.length} {data.length === 1 ? 'item' : 'itens'}
          </Text>
        </Box>
      )}
    </Box>
  )
}
